import type {
  GamePlayerProjectionTrading,
  GetProjectionAuditDataAuditedChanges,
  TradeHistoryAuditedChanges,
  TradeHistoryData
} from '@library/types/trading';
import { useEffect, useMemo, useState } from 'react';
import {
  useGetAutoTradeHistoryProjectionsQuery,
  useGetProjectionAuditQuery,
  useGetSuggestedLinesQuery
} from '@bo-services/autoTradeApi';
import { DateTime } from 'luxon';
import type { SelectOptions } from '@library/types/utils';
import { findProjectionByProjectionId } from '@bo-utils/projection';

export function useAutoTradeHistoryDrawerData({
  drawerOpen,
  durationId,
  fromDateString,
  gameId,
  leagueId,
  playerId
}: {
  drawerOpen: boolean;
  durationId: string;
  fromDateString?: string;
  gameId: string;
  leagueId?: string;
  playerId?: string;
}) {
  // format request & make first GET projections request
  const fromDate = fromDateString
    ? DateTime.fromISO(fromDateString)
    : DateTime.now();

  const { data: autoTradeHistoryProjectionsData, isFetching } =
    useGetAutoTradeHistoryProjectionsQuery(
      {
        durationId,
        fromDate: fromDate.toISODate() ?? '',
        gameId,
        leagueId: leagueId ?? '',
        playerId: playerId ?? '',
        search: '',
        toDate: fromDate.plus({ days: 4 }).toISODate() ?? undefined
      },
      { skip: !drawerOpen || !leagueId || !playerId }
    );

  const [projections, setProjections] = useState<
    Array<GamePlayerProjectionTrading>
  >([]);

  const [projection, setProjection] = useState<GamePlayerProjectionTrading>();

  const {
    projectionId: selectedProjectionId,
    projectionValue: selectedProjectionValue
  } = projection ?? {};

  useEffect(() => {
    if (!isFetching && autoTradeHistoryProjectionsData) {
      setProjections(autoTradeHistoryProjectionsData);
    }
  }, [isFetching, autoTradeHistoryProjectionsData]);

  const sortedAutoTradeHistoryProjectionsData: Array<GamePlayerProjectionTrading> =
    useMemo(() => {
      return [...projections].sort(
        (a, b) =>
          a.statType.localeCompare(b.statType) ||
          a.oddsType.localeCompare(b.oddsType)
      );
    }, [projections]);

  useEffect(() => {
    if (sortedAutoTradeHistoryProjectionsData) {
      setProjection(sortedAutoTradeHistoryProjectionsData[0]);
    }
  }, [sortedAutoTradeHistoryProjectionsData]);

  const { data: suggestedLines } = useGetSuggestedLinesQuery(
    {
      projectionId: selectedProjectionId ?? ''
    },
    { skip: !selectedProjectionId }
  );

  const { data: audits } = useGetProjectionAuditQuery(
    {
      projectionId: selectedProjectionId ?? ''
    },
    { skip: !selectedProjectionId }
  );

  const tradeHistoryData = audits?.data
    ?.filter((audit) => audit.attributes.action === 'update')
    .map((audit) => {
      let source: string = audit.attributes.service;
      if (!source) {
        source = audit.attributes.user_email || '-';
      }
      const changesData = JSON.parse(
        JSON.stringify(audit.attributes.audited_changes)
      ) as GetProjectionAuditDataAuditedChanges;
      let changes = changesData;
      if (Array.isArray(changesData.trade_settings)) {
        const tradeSettings = changesData.trade_settings.map(
          (setting: { auto_trade: boolean }) => setting.auto_trade
        );
        delete changesData.trade_settings;
        changes = {
          ...changesData,
          auto_trade: tradeSettings
        } as TradeHistoryAuditedChanges;
      }

      return {
        id: audit.id,
        source,
        auditedChanges: changes,
        comment: audit.attributes.comment || '-',
        timestamp: audit.attributes.created_at
      };
    }) as TradeHistoryData[];

  const statTypeSelectOptions: SelectOptions =
    sortedAutoTradeHistoryProjectionsData?.map((proj) => ({
      label: proj.statType,
      key: proj.projectionId,
      name: `${proj.statType} - ${proj.projectionId}`,
      value: proj.projectionId,
      oddsType: proj.oddsType
    })) ?? [];

  const handleSelectStatType = (projectionId: string) => {
    const selectedProjection = findProjectionByProjectionId({
      projectionArray: sortedAutoTradeHistoryProjectionsData ?? [],
      projectionId
    });

    if (selectedProjection) {
      setProjection(selectedProjection);
    }
  };

  return {
    isFetching,
    handleSelectStatType,
    projection,
    selectedProjectionValue,
    statTypeSelectOptions,
    suggestedLines,
    tradeHistoryData
  };
}
